import { Autocomplete, Checkbox, FormControlLabel, Grid, TextField, Tooltip } from '@mui/material';
import axios from 'axios';
import { confirmDialog } from 'common/ComfirmationDialog';
import * as Constant from 'common/Constant';
import { RENEWAL_ACCOUNT_TYPE_OPTIONS } from 'common/Constant';
import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import * as ValidationUtil from 'utils/ValidationUtil';
import { AppConfig } from '../../../../AppConfig';

export const FILTER_OPTION_FIELDS = [
  { label: 'Date Basis Filter', name: 'dateBasisEnabled' },
  { label: 'Date Range Filter', name: 'dateRangeEnabled' },
  { label: 'Rolling 12 Filter', name: 'rolling12Enabled' },
  { label: 'Plan YTD Vs Prior Plan YTD Filter', name: 'planYTDVsPriorPlanYTDEnabled' },
  { label: 'Plan YTD Vs Prior Plan Year Filter', name: 'planYTDVsPriorPlanYearEnabled' },
  { label: 'Custom Range Filter', name: 'customRangeEnabled' },
  { label: 'Divisions Filter', name: 'divisionsEnabled' },
  { label: 'Plans Filter', name: 'plansEnabled' },
  { label: 'Cohort Builder Filter', name: 'cohortBuilderEnabled' },
];

const TenantCreationDetail = () => {
  const { control, getValues, setValue } = useFormContext();
  const currentId = getValues('id');
  const stateList = useSelector((state) => state.tenantManagement.stateList);
  const [brokerOptions, setBrokerOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const brokerList = useSelector((state) => state.user.allBrokers);
  const [isActive, setIsActive] = useState(true);
  const [isLive, setIsLive] = useState(false);

  const onInputChange = (inputValue) => {
    const code = generateValidCode(inputValue);
    setValue('code', code);
  };
  const generateValidCode = (code) => {
    // Replace all uppercase characters with lowercase and whitespace and symbols with ''
    return code.replace(/[^a-zA-Z0-9_.]/g, '').toLowerCase();
  };

  const handleActiveChange = (event) => {
    const checked = event.target.checked;
    setIsActive(checked);

    if (!checked) {
      setIsLive(false);
      setValue('live', false);
    }
  };

  useEffect(() => {
    if (brokerList && Array.isArray(brokerList)) {
      setBrokerOptions(brokerList.sort((a, b) => (a.name?.toLowerCase() < b.name?.toLowerCase() ? -1 : 0)));
      if (currentId) {
        const selectedBroker = brokerList.find((broker) => broker.id === getValues('brokerId'));
        setValue('brokerId', selectedBroker);
      }
    }
  }, [brokerList]);

  useEffect(() => {
    if (stateList && Array.isArray(stateList)) {
      setStateOptions(stateList.sort((a, b) => (a.state?.toLowerCase() < b.state?.toLowerCase() ? -1 : 0)));
      if (currentId) {
        const selectedState = stateList.find((state) => state.stateId === getValues('stateId'));
        setValue('stateId', selectedState);
      }
    }
  }, [stateList]);

  useEffect(() => {
    if (RENEWAL_ACCOUNT_TYPE_OPTIONS && Array.isArray(RENEWAL_ACCOUNT_TYPE_OPTIONS)) {
      if (currentId) {
        const selectedType = RENEWAL_ACCOUNT_TYPE_OPTIONS.find((state) => state.value === getValues('renewalAccountType'));
        setValue('renewalAccountType', selectedType);
      }
    }
  }, [RENEWAL_ACCOUNT_TYPE_OPTIONS]);

  useEffect(() => {
    setIsActive(getValues('active') !== false);
    setIsLive(Boolean(getValues('live')));
    if (getValues('filtersEnabled') === undefined) {
      setValue('filtersEnabled', true);
    }
    FILTER_OPTION_FIELDS.forEach(({ name, defaultValue = true }) => {
      if (getValues(name) === undefined) {
        setValue(name, defaultValue);
      }
    });
  }, [currentId, getValues, setValue]);

  return (
    <>
      <Grid container spacing={4}>
        <Grid item xs={6}>
          <Controller
            rules={{
              required: Constant.ERROR_MSG_REQUIRED_FIELD,
              validate: async (value) => {
                const found = await axios.post(`${AppConfig.FO_INITIAL_URL}/company-admin/find-by-name`, { tenantName: value }, AppConfig.POST_HEADERS);
                return ValidationUtil.VALIDATION_DUPLICATION(found, parseInt(getValues('id')), 'Duplicated tenant name is found.');
              }
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                id="input-name"
                required
                fullWidth
                {...field}
                error={error}
                helperText={error?.message ?? null}
                label="Name"
                onChange={(event) => {
                  const value = event.target.value;
                  field.onChange(value);
                  if (!currentId) {
                    onInputChange(value);
                  }
                }}
              />
            )}
            name="name"
            control={control}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            rules={{
              required: Constant.ERROR_MSG_REQUIRED_FIELD,
              validate: {
                rule1: (value) => {
                  if (!currentId && value.includes(' ')) {
                    return 'Code cannot have white space!';
                  }
                },
                rule2: async (value) => {
                  if (!currentId) {
                    const found = await axios.post(`${AppConfig.FO_INITIAL_URL}/company-admin/find-by-code`, { tenantCode: value }, AppConfig.POST_HEADERS);

                    return ValidationUtil.VALIDATION_DUPLICATION(found, parseInt(getValues('id')), 'Duplicated tenant code is found.');
                  }
                }
              }
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                id="input-code"
                required
                fullWidth
                {...field}
                disabled={currentId}
                error={error}
                value={field.value || ''}
                helperText={error?.message ?? null}
                label="Code"
                onChange={(e) => onInputChange(e.target.value, field)}
              />
            )}
            name="code"
            control={control}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            render={({ field, fieldState: { error } }) => (
              <TextField id="input-address" fullWidth {...field} error={error} helperText={error?.message ?? null} label="Address" multiline />
            )}
            name="address"
            control={control}
          />
        </Grid>

        <Grid item xs={6}>
          <Controller
            name="stateId"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                id="input-stateId"
                options={stateOptions}
                filterSelectedOptions
                getOptionLabel={(option) => option?.state || ''}
                isOptionEqualToValue={(option, value) => option?.stateId === value?.stateId}
                renderOption={(props, option) => (
                  <li {...props} key={option?.stateId}>{option?.state || ''}</li>
                )}
                     slotProps = {{
                          listbox: {
                              sx: {
                                '& .MuiAutocomplete-option, & .MuiAutocomplete-option *, && .MuiAutocomplete-option[aria-selected="true"], & .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover ': {
                                  color: '#000000 !important'
                                },
                                '& .MuiAutocomplete-option[aria-selected="true"]': {
                                  backgroundColor: '#5470b8 !important',
                                },
                                '& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover': {
                                  backgroundColor: '#f4f5f6 !important',
                                }
                              }
                          }
                      }}
                renderInput={(params) => <TextField {...params} label="State" inputProps={{ ...params.inputProps }} />}
                onChange={(_, data) => {
                  field.onChange(data);
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            name="brokerId"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                id="input-brokerId"
                options={brokerOptions}
                filterSelectedOptions
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                renderOption={(props, option) => (
                  <li {...props} key={option?.id}>{option?.name || ''}</li>
                )}
                 slotProps = {{
                    listbox: {
                        sx: {
                          '& .MuiAutocomplete-option[aria-selected="true"]': {
                            backgroundColor: '#5470b8 !important',
                            color: '#ffffff !important'
                          },
                          '& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover': {
                            backgroundColor: '#f4f5f6 !important',
                            color: '#000000 !important'
                          }
                        }
                    }
                }}
                renderInput={(params) => <TextField {...params} label="Broker" inputProps={{ ...params.inputProps }} />}
                onChange={(_, data) => {
                  field.onChange(data);
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            rules={{
              required: Constant.ERROR_MSG_REQUIRED_FIELD
            }}
            name="renewalAccountType"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                id="input-renewalAccountType"
                options={RENEWAL_ACCOUNT_TYPE_OPTIONS}
                filterSelectedOptions
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderOption={(props, option) => (
                  <li {...props} key={option?.value}>{option.name}</li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Renewal Account Type" inputProps={{ ...params.inputProps }} required error={error} helperText={error ? error.message : null} />
                )}
                slotProps = {{
                      listbox: {
                          sx: {
                            '& .MuiAutocomplete-option, & .MuiAutocomplete-option *, && .MuiAutocomplete-option[aria-selected="true"], & .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover ': {
                              color: '#000000 !important'
                            },
                            '& .MuiAutocomplete-option[aria-selected="true"]': {
                              backgroundColor: '#5470b8 !important',
                            },
                            '& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover': {
                              backgroundColor: '#f4f5f6 !important',
                            }
                          }
                      }
                  }}
                onChange={(_, data) => {
                  if (currentId) {
                    confirmDialog(
                      () => {
                        field.onChange(data);
                      },
                      `Please confirm your selection, these changes will affect case processing.`,
                      'Confirm Selection',
                      'Cancel',
                      'Confirm',
                      () => {}
                    );
                  } else {
                    field.onChange(data);
                  }
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={6}></Grid>
        {!currentId && (
          <>
            <Grid item lg={6}>
              <Controller
                render={({ field, fieldState: { error } }) => (
                  <FormControlLabel
                    {...field}
                    control={
                      <Checkbox
                        color="primary"
                        defaultChecked
                        onChange={(e) => {
                          field.onChange(e);
                          handleActiveChange(e);
                        }}
                      />
                    }
                    label="Active"
                    labelPlacement="end"
                  />
                )}
                defaultValue={true}
                control={control}
                name="active"
              />
            </Grid>
            <Grid item lg={6}>
              <Controller
                render={({ field, fieldState: { error } }) => (
                  <Tooltip title={!isActive ? 'Active status is required' : ''}>
                    <span>
                      <FormControlLabel
                        {...field}
                        control={
                          <Checkbox
                            color="primary"
                            defaultChecked
                            checked={isLive}
                            disabled={!isActive}
                            onChange={(e) => {
                              field.onChange(e.target.checked);
                              setIsLive(e.target.checked);
                            }}
                          />
                        }
                        label="Go-live"
                        labelPlacement="end"
                      />
                    </span>
                  </Tooltip>
                )}
                control={control}
                name="live"
              />
            </Grid>
          </>
        )}
        <Grid item lg={6}>
          <Controller
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label="AI Summary Enabled"
                labelPlacement="end"
              />
            )}
            defaultValue={true}
            control={control}
            name="aiSummaryEnabled"
          />
          <Controller
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label="Use Cohort Builder PHI"
                labelPlacement="end"
              />
            )}
            defaultValue={false}
            control={control}
            name="phiCohortBuilderEnabled"
          />
        </Grid>
        <Grid item lg={6}>
          <Controller
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label="Workday Integration Enabled"
                labelPlacement="end"
              />
            )}
            defaultValue={false}
            control={control}
            name="campaignEnabled"
          />
        </Grid>
        <Grid item lg={6}>
          <Grid container direction="column">
            <Controller
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox color="primary" checked={field.value !== false} onChange={(e) => field.onChange(e.target.checked)} />}
                  label="Filters Enabled"
                  labelPlacement="end"
                />
              )}
              defaultValue={true}
              control={control}
              name="filtersEnabled"
            />
            {FILTER_OPTION_FIELDS.map(({ label, name, defaultValue = true }) => (
              <Controller
                key={name}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <span style={{ paddingRight: 6, paddingLeft: 12 }}>-</span>
                        <Checkbox
                          color="primary"
                          checked={field.value == null ? defaultValue : Boolean(field.value)}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      </span>
                    }
                    label={label}
                  />
                )}
                defaultValue={defaultValue}
                control={control}
                name={name}
              />
            ))}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default TenantCreationDetail;
